/* eslint-disable react/jsx-indent */
import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import useOrders from '../../../hooks/useOrders';

import { useApi } from '../../../contexts/ApiContext';
import { useMenus } from '../../../contexts/SiteContext';
import { useUser } from '../../../contexts/UserContext';
import StyledButton from '../../buttons/StyledButton';
import Order from '../../cards/Order';
import Loader from '../../partials/Loader';
import PageMeta from '../../partials/PageMeta';
import Sidebar from '../../partials/Sidebar';

import styles from '../../../../styles/pages/pro.module.css';

const propTypes = { page: PropTypes.shape({ id: PropTypes.string }) };

function ProOrdersPage({ page = null }) {
    const route = useUrlGenerator();
    const user = useUser();
    const api = useApi();
    const { email = null, email_verified: emailVerified = false } = user || {};

    const { pro } = useMenus();
    const { title = null } = page || {};
    const { data: orders = null, isLoading = false } = useOrders({
        query: { order: ['created_at', 'desc'] },
    });

    const [confirmDisabled, setConfirmDisabled] = useState(false);
    const onClickConfirm = useCallback(() => {
        if (api !== null) {
            api.auth.requestConfirm(email);
            setConfirmDisabled(true);
        }
    }, [api, email, setConfirmDisabled]);

    return (
        <div className={styles.container}>
            <PageMeta page={page} />
            <Sidebar className={styles.sidebar} menu={pro} title={title}>
                <div className={styles.top}>
                    {emailVerified ? (
                        <StyledButton
                            className={styles.button}
                            href={route('pro.orders.create')}
                            size="small"
                        >
                            <FormattedMessage
                                defaultMessage="Make a new order"
                                description="New order button"
                            />
                        </StyledButton>
                    ) : (
                        <div>
                            <p>
                                <FormattedMessage
                                    defaultMessage="You need to confirm your email address to create orders. Check your inbox (and maybe spam folder) for the confirmation email and click on the link within."
                                    description="Email confirmation message"
                                />
                            </p>
                            <StyledButton
                                className={classNames([styles.button, styles.back])}
                                onClick={onClickConfirm}
                                disabled={confirmDisabled}
                            >
                                <FormattedMessage
                                    defaultMessage="Resend email confirmation to {email}"
                                    description="New order button"
                                    values={{ email }}
                                />
                            </StyledButton>
                        </div>
                    )}
                </div>
                <hr className={styles.spacer} />
                <div className={styles.gridFlat}>
                    {emailVerified && orders !== null && orders.length > 0
                        ? (orders || []).map((order) => (
                              <Order
                                  className={styles.item}
                                  key={`order-${order?.id}`}
                                  item={order}
                              />
                          ))
                        : null}
                </div>
                {isLoading ? <Loader /> : null}
                {!isLoading && emailVerified && orders !== null && orders.length === 0 ? (
                    <p>
                        <FormattedMessage
                            defaultMessage="You have no orders yet."
                            description="No orders message"
                        />
                    </p>
                ) : null}
            </Sidebar>
        </div>
    );
}

ProOrdersPage.propTypes = propTypes;

export default ProOrdersPage;
